import { useState, useEffect } from 'react';
import { User, Mail, BookOpen, Save, X, Edit, Lock } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ConfirmationModal from '../../components/ConfirmationModal';
import Toast from '../../components/Toast';

export default function LecturerProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ full_name: '', email: '', phone_no: '' });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get('/lecturer/profile');
      if (res.data.success && res.data.data && res.data.data.profile) {
        const profileData = res.data.data.profile;
        setProfile(profileData);
        setFormData({
          full_name: profileData.full_name || '',
          email: profileData.email || '',
          phone_no: profileData.phone_no || ''
        });
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    try {
      const res = await api.put('/lecturer/profile', formData);
      if (res.data.success) {
        setSuccess('Profile updated successfully!');
        setEditing(false);
        loadProfile();
        // Update local storage user info
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        storedUser.fullName = formData.full_name;
        storedUser.email = formData.email;
        localStorage.setItem('user', JSON.stringify(storedUser));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    }
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    setPasswordError('');

    // Basic validation
    if (passwords.new !== passwords.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwords.new.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    // Ask for confirmation before performing the change
    setShowConfirmModal(true);
  };

  const performPasswordChange = async () => {
    setPasswordError('');
    try {
      const res = await api.put('/lecturer/password', {
        currentPassword: passwords.current,
        newPassword: passwords.new
      });

      if (res.data.success) {
        setToastType('success');
        setToastMessage('Password changed successfully!');
        setShowToast(true);
        setPasswords({ current: '', new: '', confirm: '' });
        setShowPasswordModal(false);
      } else {
        setToastType('error');
        setToastMessage(res.data.message || 'Failed to change password');
        setShowToast(true);
      }
    } catch (err) {
      setToastType('error');
      setToastMessage(err.response?.data?.message || 'Failed to change password');
      setShowToast(true);
    }
  };

  const cancelEdit = () => {
    setFormData({
      full_name: profile?.full_name || '',
      email: profile?.email || '',
      phone_no: profile?.phone_no || ''
    });
    setEditing(false);
    setError('');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">My Profile</h1>
          <p className="text-gray-600">View and edit your personal information</p>
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
            <Edit className="w-4 h-4" /> Edit Profile
          </button>
        )}
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Profile Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Header Banner */}
        <div className="h-32 bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500"></div>
        
        {/* Profile Info */}
        <div className="px-6 pb-6">
          <div className="flex flex-col items-center -mt-16">
            <div className="w-32 h-32 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center">
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                <span className="text-white font-bold text-4xl">{profile?.full_name?.charAt(0) || 'L'}</span>
              </div>
            </div>
            {!editing ? (
              <>
                <h2 className="mt-4 text-2xl font-bold text-gray-800">{profile?.full_name}</h2>
                <span className="mt-1 px-4 py-1 bg-purple-100 text-purple-600 rounded-full text-sm font-medium">Lecturer</span>
              </>
            ) : (
              <div className="mt-4 w-full max-w-sm">
                <input type="text" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className="w-full text-center text-2xl font-bold text-gray-800 border-b-2 border-indigo-500 focus:outline-none pb-1" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">Email Address</p>
                {!editing ? (
                  <p className="font-medium text-gray-800">{profile?.email}</p>
                ) : (
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full font-medium text-gray-800 bg-white border border-gray-300 rounded-lg px-3 py-1 focus:ring-2 focus:ring-indigo-500" />
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="p-3 bg-green-100 rounded-xl">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">Phone Number</p>
                {!editing ? (
                  <p className="font-medium text-gray-800">{profile?.phone_no || '-'}</p>
                ) : (
                  <input type="tel" value={formData.phone_no} onChange={(e) => setFormData({...formData, phone_no: e.target.value})}
                    className="w-full font-medium text-gray-800 bg-white border border-gray-300 rounded-lg px-3 py-1 focus:ring-2 focus:ring-indigo-500" />
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="p-3 bg-purple-100 rounded-xl">
                <BookOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">Assigned Subjects</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {profile?.subjects ? profile.subjects.split(', ').map((s, i) => (
                    <span key={i} className="px-2 py-1 bg-indigo-100 text-indigo-600 rounded-full text-xs font-medium">{s}</span>
                  )) : <span className="text-gray-400">No subjects assigned</span>}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="p-3 bg-orange-100 rounded-xl">
                <User className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">User ID</p>
                <p className="font-medium text-gray-800">#{profile?.user_id}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {editing ? (
            <div className="mt-6 flex gap-3">
              <button onClick={cancelEdit} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2">
                <X className="w-4 h-4" /> Cancel
              </button>
              <button onClick={handleSave} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2">
                <Save className="w-4 h-4" /> Save Changes
              </button>
            </div>
          ) : (
            <div className="mt-6">
              <button onClick={() => setShowPasswordModal(true)} className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2 shadow-sm">
                <Lock className="w-4 h-4" /> Change Password
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowPasswordModal(false)}></div>
          <div className="relative bg-white rounded-lg shadow-md w-full max-w-md mx-4 p-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Change Password</h2>

            {passwordError && <div className="mb-3 text-sm text-red-600">{passwordError}</div>}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input type="password" required value={passwords.current} onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input type="password" required value={passwords.new} onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${passwords.new.length>0 && passwords.new.length<8 ? 'border border-red-300' : passwords.new.length>=8 ? 'border border-green-300' : 'border border-gray-300'}`} />
                {passwords.new.length>0 && passwords.new.length<8 && <p className="text-sm text-red-600 mt-1">Must be at least 8 characters</p>}
                {passwords.new.length>=8 && <p className="text-sm text-green-600 mt-1">Password length OK</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input type="password" required value={passwords.confirm} onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${passwords.confirm.length>0 && passwords.confirm!==passwords.new ? 'border border-red-300' : passwords.confirm.length>0 && passwords.confirm===passwords.new && passwords.new.length>=8 ? 'border border-green-300' : 'border border-gray-300'}`} />
                {passwords.confirm.length>0 && passwords.confirm!==passwords.new && <p className="text-sm text-red-600 mt-1">Passwords do not match</p>}
                {passwords.confirm.length>0 && passwords.confirm===passwords.new && passwords.new.length>=8 && <p className="text-sm text-green-600 mt-1">Passwords match</p>}
              </div>
              <div className="flex gap-3 pt-3">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Change Password</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation modal for password change */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          performPasswordChange();
          setShowConfirmModal(false);
        }}
        title="Confirm password change"
        message="Are you sure you want to change your password?"
        confirmText="Change"
        cancelText="Cancel"
        type="info"
      />

      {/* Toast */}
      <div className="fixed top-4 right-4 w-full max-w-sm z-50">
        <Toast message={toastMessage} type={toastType} visible={showToast} onClose={() => setShowToast(false)} />
      </div>
    </div>
  );
}
